import { Switch as BaseSwitch } from '@base-ui/react/switch'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { useFieldRequired } from '../field/nested/context'
import type { PartProps } from '../../lib/part-props'

/**
 * One height sets the size and everything else follows from it: track 9/4 h, thumb 5/4 h wide,
 * inset h/8 rounded down to a whole pixel, and the thumb as tall as the track less that inset on
 * each side. A whole-pixel inset keeps the thumb dead centre at any pixel ratio; a half pixel
 * rounds differently above and below and the thumb reads low. Two sizes because a toggle holds no
 * text, so past 20px the track only gets louder.
 *
 * The thumb stretches by width, not scale: scaling a pill flattens its ends into an oval. Its left
 * edge shifts back by throw x the extra width, so the stretch grows rightward from off, leftward
 * from on, and from the centre mid-travel. Layout containment keeps that per-frame width change
 * inside the track.
 */
const toggleVariants = cva(
  [
    'relative inline-block shrink-0 cursor-pointer rounded-full [contain:layout]',
    'h-[var(--toggle-h)] w-[calc(var(--toggle-h)*9/4)] [--toggle-inset:round(down,calc(var(--toggle-h)/8),1px)]',
    // Two steps above bg-element: the thumb is white, and on a-3 the off track all but vanishes.
    'bg-[color:var(--gray-a-5)] hover:bg-[color:var(--gray-a-6)] active:bg-[color:var(--gray-a-7)]',
    'data-[checked]:bg-info data-[checked]:hover:bg-info-hover data-[checked]:active:bg-info-active',
    // Disabled reads like a disabled Button: the fill drops to the disabled wash in both positions,
    // so the thumb's side carries the answer, the cursor says not-allowed, and hover and press do
    // nothing. Important because the checked hover fills outrank a single data variant.
    'data-[disabled]:!bg-element-disabled data-[disabled]:cursor-not-allowed data-[disabled]:![--toggle-press:0]',
    // One registered number, --toggle-throw, drives the whole move: the thumb's position reads it
    // linearly and its stretch through a sine, so it lengthens mid-travel and settles at both ends.
    '[--toggle-throw:0] data-[checked]:[--toggle-throw:1]',
    // Held down, the thumb stretches toward where it will go, anchored at the edge it rests on.
    '[--toggle-press:0] active:[--toggle-press:1] motion-reduce:active:[--toggle-press:0]',
    'transition-[background-color,--toggle-throw,--toggle-press] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
  ],
  {
    variants: {
      size: {
        sm: '[--toggle-h:16px]',
        md: '[--toggle-h:20px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

interface ToggleProps extends PartProps<BaseSwitch.Root.Props>, VariantProps<typeof toggleVariants> {
  className?: string
}

/**
 * A setting that applies the moment it flips; no Save under it. Use a Checkbox when the choice is
 * collected and submitted later.
 */
function Toggle({ className, size, required, ...props }: ToggleProps) {
  return (
    <BaseSwitch.Root
      required={useFieldRequired(required)}
      className={twMerge(toggleVariants({ size }), className)}
      {...props}
    >
      <BaseSwitch.Thumb
        className={twMerge(
          'bg-on-dark shadow-xs absolute left-[var(--toggle-inset)] top-[var(--toggle-inset)] rounded-full',
          '[--toggle-stretch:calc(0.16*sin(var(--toggle-throw)*180deg)+0.2*var(--toggle-press))] [--toggle-thumb-w:calc(var(--toggle-h)*5/4)]',
          'h-[calc(var(--toggle-h)-2*var(--toggle-inset))] w-[calc(var(--toggle-thumb-w)*(1+var(--toggle-stretch)))]',
          // Travel is the track less the thumb and both insets, less the stretch so the far edge holds.
          '[translate:calc(var(--toggle-throw)*(var(--toggle-h)-2*var(--toggle-inset)-var(--toggle-thumb-w)*var(--toggle-stretch)))_0]',
        )}
      />
    </BaseSwitch.Root>
  )
}

export { Toggle, toggleVariants }
export type { ToggleProps }
